'use client';

/**
 * FieldStudio — Phase 1 of the per-program field configuration roadmap.
 *
 * One surface that shows, for a chosen program, how the *same* configured
 * field set renders across the artifacts staff care about, side by side:
 *   - Member Profile  (grouped, configured fields with sample values)
 *   - A Form          (live FormRenderer with conditional visibility)
 *   - Member List + Advanced Filters
 *
 * Everything is read-only preview. Per-program overrides (Phase 0) are honored
 * because every config endpoint is queried with program_id, so a field hidden
 * for the program drops from all panels at once. The Form panel is interactive
 * so visibility_rules can be exercised live as you type.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  membersAPI,
  ProfileField,
  MemberListColumn,
  MemberAdvancedFilter,
} from '@/services/api/members';
import { programsAPI, Program } from '@/services/api/programs';
import { formsAPI, FormTemplate } from '@/services/api/forms';
import { FormRenderer } from '@/components/forms/FormRenderer';

// listPrograms can return cross-org programs for super_admins; scope to the
// active cbo so the preview only offers this org's programs.
function filterToActiveCbo(programs: Program[]): Program[] {
  const active = Number(localStorage.getItem('active_cbo_id'));
  return active ? programs.filter((p) => p.cbo_id === active) : programs;
}

// A plausible sample value for a field, so previews look real without needing
// an actual member record.
function sampleValue(fieldType: string, label: string): string {
  const t = (fieldType || '').toLowerCase();
  if (t.includes('date')) return '2026-01-15';
  if (t.includes('time')) return '09:30 AM';
  if (t === 'int' || t === 'integer' || t === 'number') return '42';
  if (t === 'bool' || t === 'toggle' || t === 'checkbox') return 'Yes';
  if (t.includes('email')) return 'sample@example.org';
  if (t.includes('phone')) return '(555) 010-1234';
  if (t === 'select' || t === 'multi_select' || t === 'radio') return 'Option A';
  if (t.includes('ssn')) return '•••-••-1234';
  return `Sample ${(label || 'value').toLowerCase()}`;
}

interface ProfileCategory {
  key: string;
  name: string;
  sort: number;
  fields: ProfileField[];
}

function groupByCategory(fields: ProfileField[]): ProfileCategory[] {
  const map = new Map<string, ProfileCategory>();
  fields.forEach((f) => {
    let cat = map.get(f.category_key);
    if (!cat) {
      cat = { key: f.category_key, name: f.category_name, sort: f.category_sort, fields: [] };
      map.set(f.category_key, cat);
    }
    cat.fields.push(f);
  });
  const cats = Array.from(map.values());
  cats.sort((a, b) => a.sort - b.sort);
  cats.forEach((c) => c.fields.sort((a, b) => a.field_sort - b.field_sort));
  return cats;
}

export default function FieldStudio() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programId, setProgramId] = useState<number | null>(null);

  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [listColumns, setListColumns] = useState<MemberListColumn[]>([]);
  const [advFilters, setAdvFilters] = useState<MemberAdvancedFilter[]>([]);
  const [forms, setForms] = useState<FormTemplate[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    programsAPI
      .listPrograms({ is_active: true })
      .then((p) => setPrograms(filterToActiveCbo(p)))
      .catch((e) => setError(e?.message || 'Failed to load programs'));
  }, []);

  const load = useCallback(async (pid: number) => {
    setLoading(true);
    setError(null);
    try {
      const [pf, lc, af, fm] = await Promise.all([
        membersAPI.getProfileFields({ program_id: pid }),
        membersAPI.getMemberListColumns('member', pid),
        membersAPI.getMemberAdvancedFilters('member', pid),
        formsAPI.getFormsByProgram(pid, true),
      ]);
      setProfileFields(pf);
      setListColumns(lc);
      setAdvFilters(af);
      const withSections = fm.filter((f) => (f.sections?.length ?? 0) > 0);
      setForms(withSections);
      setSelectedFormId(withSections[0]?.id ?? null);
      setFormValues({});
    } catch (e: any) {
      setError(e?.message || 'Failed to load preview data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (programId != null) load(programId);
  }, [programId, load]);

  const categories = useMemo(() => groupByCategory(profileFields), [profileFields]);
  const selectedForm = useMemo(
    () => forms.find((f) => f.id === selectedFormId) ?? null,
    [forms, selectedFormId]
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Field Studio — Multi-Artifact Preview</CardTitle>
          <p className="text-sm text-muted-foreground">
            Pick a program and see how its configured fields render across the
            member profile, a form, and the member list — all at once. Per-program
            overrides are applied, so hiding a field for this program removes it
            everywhere here. The form is interactive so you can exercise
            conditional visibility live.
          </p>
        </CardHeader>
        <CardContent>
          <div className="w-72">
            <Select
              value={programId != null ? String(programId) : ''}
              onValueChange={(v) => setProgramId(v ? Number(v) : null)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a program…" />
              </SelectTrigger>
              <SelectContent>
                {programs.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </CardContent>
      </Card>

      {programId == null ? (
        <p className="text-sm text-muted-foreground">
          Select a program to preview its artifacts.
        </p>
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Loading preview…</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* Member Profile */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Member Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {categories.length === 0 && (
                <p className="text-sm text-muted-foreground">No visible profile fields.</p>
              )}
              {categories.map((cat) => (
                <div key={cat.key}>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {cat.name}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {cat.fields.map((f) => (
                      <div key={`${f.is_core_field ? 'c' : 'x'}:${f.field_key}`} className="text-sm">
                        <div className="text-xs text-muted-foreground">
                          {f.display_name}
                          {f.is_required && <span className="ml-1 text-red-500">*</span>}
                        </div>
                        <div>{sampleValue(f.field_type, f.display_name)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Form</CardTitle>
              {forms.length > 0 && (
                <div className="w-full pt-2">
                  <Select
                    value={selectedFormId != null ? String(selectedFormId) : ''}
                    onValueChange={(v) => {
                      setSelectedFormId(v ? Number(v) : null);
                      setFormValues({});
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a form…" />
                    </SelectTrigger>
                    <SelectContent>
                      {forms.map((f) => (
                        <SelectItem key={f.id} value={String(f.id)}>
                          {f.name} ({f.form_type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {selectedForm ? (
                <FormRenderer
                  sections={selectedForm.sections}
                  value={formValues}
                  onChange={(fieldId, v) =>
                    setFormValues((prev) => ({ ...prev, [fieldId]: v }))
                  }
                  banner={
                    <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      Preview mode — entries are not saved.
                    </div>
                  }
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No forms with fields are linked to this program yet.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Member List + Filters */}
          <Card className="xl:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Member List &amp; Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {advFilters.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {advFilters.map((f) => (
                    <Badge key={f.field_key} variant="outline">
                      {f.display_name}
                    </Badge>
                  ))}
                </div>
              )}
              {listColumns.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No fields are flagged to show in the list for this program.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {listColumns.map((c) => (
                        <TableHead key={c.field_key}>{c.display_name}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      {listColumns.map((c) => (
                        <TableCell key={c.field_key}>
                          {sampleValue(c.field_type, c.display_name)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
